import { useState } from "react";
import { useNavigate } from "react-router-dom";
import type { CheckItemDto } from "@mmm/contracts";
import { ListRow } from "../molecules/ListRow.js";
import { IconButton } from "../ui/IconButton.js";
import { Pill, useT } from "@mmm/ui";
export type TaskRowItem = CheckItemDto & { subjectTitle?: string | null; subjectColor?: string | null };

interface TaskRowProps {
  item: TaskRowItem;
  onToggle: (done: boolean) => void;
  onRename: (text: string) => void | Promise<unknown>;
  onDelete?: () => void;
  showSubjectTag?: boolean;
  variant?: "list" | "grid";
}

export function TaskRow({ item, onToggle, onRename, onDelete, showSubjectTag = false, variant = "list" }: TaskRowProps) {
  const navigate = useNavigate();
  const t = useT();
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(item.text);

  const startRename = () => {
    setDraft(item.text);
    setEditing(true);
  };

  const commitRename = async () => {
    const next = draft.trim();
    setEditing(false);
    if (!next || next === item.text) {
      setDraft(item.text);
      return;
    }
    try {
      await onRename(next);
    } catch {
      // Already toasted centrally (api/commands.ts's run()) — this catch
      // exists purely so a failed rename doesn't surface as an unhandled
      // promise rejection (onBlur invokes commitRename without awaiting it).
    }
  };

  return (
    <ListRow
      variant={variant}
      className={`mmm-task-item mmm-task-item--${variant} ${item.done ? "is-done" : ""}`.trim()}
      leading={
        <input
          type="checkbox"
          aria-label={item.text}
          checked={item.done}
          onChange={(event) => onToggle(event.target.checked)}
          className="mmm-input-accent"
        />
      }
      title={
        editing ? (
          <input
            autoFocus
            aria-label={t("tasks.renameTask")}
            className="mmm-input mmm-task-edit-input"
            value={draft}
            onChange={(event) => setDraft(event.target.value)}
            onBlur={commitRename}
            onKeyDown={(event) => {
              if (event.key === "Enter") event.currentTarget.blur();
              if (event.key === "Escape") {
                setDraft(item.text);
                setEditing(false);
              }
            }}
          />
        ) : (
          <span className="mmm-task-item-title">{item.text}</span>
        )
      }
      meta={
        showSubjectTag && item.subjectTitle && item.subjectId ? (
          <Pill
            className="mmm-task-subject-pill"
            color={item.subjectColor ?? undefined}
            variant="tag"
            onClick={() => navigate(`/subjects/${item.subjectId}/tasks`)}
          >
            {item.subjectTitle}
          </Pill>
        ) : undefined
      }
      trailing={
        <div className="mmm-item-actions">
          {!editing && (
            <IconButton icon="edit" label={t("tasks.renameTask")} className="mmm-row-action-btn" onClick={startRename} />
          )}
          {onDelete && (
            <IconButton
              icon="trash"
              label={t("tasks.deleteTask")}
              className="mmm-row-action-btn mmm-row-action-btn--danger"
              onClick={onDelete}
            />
          )}
        </div>
      }
    />
  );
}
